import { baseApi } from '@/features/api/baseApi';
import { type RequestQuery } from '@/features/api/directus';
import {
  type ReadOneQueryArg,
  buildCountFn,
  buildCreateFn,
  buildDeleteFn,
  buildDeleteManyFn,
  buildReadManyFn,
  buildReadOneFn,
  buildUpdateFn,
  buildUpdateManyFn,
} from '@/features/api/endpoints/createDirectusEndpoints';
import type { Role } from '@/models/types';

const roleApi = baseApi
  .enhanceEndpoints({
    addTagTypes: ['role', 'roles'],
  })
  .injectEndpoints({
    endpoints: builder => ({
      getRoles: builder.query<Role[], RequestQuery<Role>>({
        queryFn: buildReadManyFn<Role>('Role'),
        providesTags: ['roles'],
      }),
      getRole: builder.query<Role, ReadOneQueryArg<Role, number>>({
        queryFn: buildReadOneFn<Role, number>('Role'),
        providesTags: ['role'],
      }),
      getRoleCount: builder.query<number, RequestQuery<Role> | undefined>({
        queryFn: buildCountFn<Role>('Role'),
        providesTags: ['roles'],
      }),
      createRole: builder.mutation<Role, Partial<Role>>({
        queryFn: buildCreateFn<Role>('Role'),
        invalidatesTags: ['role', 'roles'],
      }),
      updateRole: builder.mutation<Role, { id: number; body: Partial<Role> }>({
        queryFn: buildUpdateFn<Role, number>('Role'),
        invalidatesTags: ['role', 'roles'],
      }),
      updateRoles: builder.mutation<Role[], { ids: number[]; body: Partial<Role> }>({
        queryFn: buildUpdateManyFn<Role, number>('Role'),
        invalidatesTags: ['role', 'roles'],
      }),
      deleteRole: builder.mutation<void, number>({
        queryFn: buildDeleteFn<number>('Role'),
        invalidatesTags: ['role', 'roles'],
      }),
      deleteRoles: builder.mutation<void, number[]>({
        queryFn: buildDeleteManyFn<number>('Role'),
        invalidatesTags: ['role', 'roles'],
      }),
    }),
  });

export const {
  useGetRolesQuery,
  useGetRoleQuery,
  useGetRoleCountQuery,
  useCreateRoleMutation,
  useUpdateRoleMutation,
  useUpdateRolesMutation,
  useDeleteRoleMutation,
  useDeleteRolesMutation,
} = roleApi;
